import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { Groups } from '@mui/icons-material';

import { WhiteHolder } from '@frontend/features/common/components/WhiteHolder';

import { useCaseTravelers } from '../queries';
import { travelerDisplayName } from '../travelerForm';
import { travelerLoadState } from '../travelerUiState';
import { CaseTravelersModal } from './CaseTravelersModal';

interface Props {
  caseId: number;
  agencyId: number;
  hasClient: boolean;
}

export const CaseTravelersSummary = ({ caseId, agencyId, hasClient }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const roster = useCaseTravelers({
    caseId,
    currentPage: 1,
    pageSize: 3,
  });
  const loadState = travelerLoadState([roster.error]);

  return (
    <>
      <WhiteHolder title={t('travelers.summary.title')}>
        {roster.isLoading ? (
          <Skeleton variant="rounded" height={64} />
        ) : loadState === 'forbidden' ? (
          <Typography variant="body2" color="text.secondary">
            {t('travelers.states.noPermission')}
          </Typography>
        ) : roster.isError ? (
          <Typography variant="body2" color="text.secondary">
            {t('travelers.states.unavailable')}
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <Groups color="action" fontSize="small" />
              <Typography variant="body2" color="text.primary">
                {t('travelers.summary.count', {
                  count: roster.data?.count ?? 0,
                })}
              </Typography>
            </Box>
            {roster.data?.items.length ? (
              <Typography variant="caption" color="text.secondary">
                {roster.data.items.map(travelerDisplayName).join(', ')}
                {(roster.data.count ?? 0) > roster.data.items.length
                  ? ` ${t('travelers.summary.more')}`
                  : ''}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('travelers.states.emptyRoster')}
              </Typography>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => setOpen(true)}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('travelers.actions.manage')}
            </Button>
          </Box>
        )}
      </WhiteHolder>

      <CaseTravelersModal
        open={open}
        caseId={caseId}
        agencyId={agencyId}
        hasClient={hasClient}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
